import { useTranslation } from "react-i18next";
import { Package, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import ErrorComponent from "./ErrorComponent";
import HeroTitle from "./HeroTitle";
import FeaturedCarousel from "./FeaturedCarousel";
import DeleteConfirmPopUp from "./DeleteConfirmPopUp";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { useMobile } from "@packages/hooks/useMobile";
import { joyrideStyles, joyrideStylesDark } from "@packages/utils";
import Joyride, { CallBackProps, Step } from "react-joyride";
import { trackEvent } from "@packages/hooks/useAnalytics";
import FeaturedCard from "../featured-card";
import Card from "../card";
import { Text, Button } from "@core/index";
import { Tool } from "./types";
import ToolHelper from "../ToolHelper";
import { useTheme } from "@packages/hooks/useTheme";

interface GenericToolPageProps {
  useGetTools: any;
  useDeleteTool: any;
  getGetToolsQueryKey: (...args: any[]) => any;
  useGetTopTools: any;
  likeTool: (id: number) => Promise<void>;
  unlikeTool: (id: number) => Promise<void>;
  getGetTopToolsQueryKey: (...args: any[]) => any;
  toolType: string;
  toolPath: string;
  maxWidth?: string;
  DetailComponent: React.ComponentType<{ tool: Tool }>;
  colors: {
    light: string;
    dark: string;
    iconBg: string;
    iconColor: string;
    hover: string;
  };
  toolColor: string;
  gradientColor: string;
  // can be number or function or undefined
  customRefetchInterval?:
    | number
    | ((query: unknown) => number | false)
    | undefined;
}

const GenericToolPage = ({
  useGetTools,
  useDeleteTool,
  getGetToolsQueryKey,
  useGetTopTools,
  likeTool,
  unlikeTool,
  getGetTopToolsQueryKey,
  toolType,
  toolPath,
  DetailComponent,
  colors,
  toolColor,
  gradientColor,
  maxWidth = "max-w-4xl",
  customRefetchInterval = undefined,
}: GenericToolPageProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [selectedToolId, setSelectedToolId] = useState<number | null>(null);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const [runTour, setRunTour] = useState(false);
  const [skipAllTours] = useLocalStorage("skip-all-tours", false);

  const [skippedTours, setSkippedTours] = useLocalStorage<
    Record<string, boolean>
  >("skipped-tours-by-type", {});
  const isMobile = useMobile();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [isTourFlowActive, setIsTourFlowActive] = useState(false);
  const [onboardingStepIndex, setOnboardingStepIndex] = useState(0);
  const { theme } = useTheme();

  const steps: Step[] = [
    {
      target: "body",
      content: t(`onboarding.${toolType}.tour.welcome`),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".featured-carousel",
      content: t(`onboarding.${toolType}.tour.liked`),
      placement: "bottom",
    },
    {
      target: ".featured-card",
      content: (
        <div className="flex flex-col items-center gap-4">
          {toolPath !== "ai-assessment-scale" && (
            <video
              src={`/tutorials_${i18n.language}/${toolPath}.mp4`}
              autoPlay
              loop
              muted
              className="w-[350px] rounded-xl"
            />
          )}
          <Text variant="p" status="subtle" size={"base"}>
            {`onboarding.${toolType}.tour.featured`}
          </Text>
        </div>
      ),
      placement: "right-start",
    },
    {
      target: ".create-button",
      content: t(`onboarding.${toolType}.tour.create`),
      placement: "bottom-end",
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { index, status, action, lifecycle } = data;

    if (action === "close") {
      setRunTour(false);
      setIsTourFlowActive(false);
      return;
    }

    if (index === 2 && lifecycle === "complete" && action === "next") {
      setSelectedToolId(Number(topTools?.data?.[0]?.id));
      setOnboardingStepIndex(3); // resume later at step 3
      setRunTour(false);
      return;
    }

    if (lifecycle === "complete" && action === "next") {
      setOnboardingStepIndex(index + 1);
    }

    if (["finished", "skipped"].includes(status)) {
      setRunTour(false);
      setIsTourFlowActive(false);
      setOnboardingStepIndex(0); // reset for next time

      if (status === "skipped") {
        setSkippedTours((prev) => ({ ...prev, [toolType]: true }));
      }

      if (status === "finished") {
        navigate(`/content/${toolPath}/create`);
      }
    }
  };

  const {
    data: tools,
    isLoading,
    error,
    refetch,
  } = useGetTools(
    {},
    {
      query: {
        refetchInterval: customRefetchInterval
          ? (query: any) => {
              if (typeof customRefetchInterval === "function") {
                return customRefetchInterval(query);
              }
              return customRefetchInterval;
            }
          : (query: any) => {
              const firstItem = query.state.data?.data?.[0];
              //updated to check if the first item is not null and if the imageUrl does not exist
              if (firstItem && !firstItem?.imageUrl) {
                return 5000;
              }
              return false;
            },
      },
    },
  );

  const { data: topTools, isLoading: isTopToolsLoading } = useGetTopTools({
    page: 1,
    size: 10,
  });

  const gradientClasses = `absolute inset-0 bg-gradient-to-br from-${toolColor}-400/60 via-${gradientColor}-500/40 to-${toolColor}-600/60 bg-blend-soft-light backdrop-blur-[2px] rounded-xl flex items-center justify-center p-4 shadow-lg shadow-${toolColor}-500/10`;

  useEffect(() => {
    const hasSkippedThisToolType = skippedTours[toolType];
    const shouldShowTour =
      !isLoading &&
      !isTopToolsLoading &&
      topTools?.data &&
      (!tools?.data || tools.data.length === 0) &&
      !skipAllTours &&
      !hasSkippedThisToolType &&
      !isMobile;
    setRunTour(shouldShowTour);
    setIsTourFlowActive(shouldShowTour);
  }, [
    isLoading,
    isTopToolsLoading,
    tools?.data,
    topTools?.data,
    skipAllTours,
    skippedTours,
    toolType,
    isMobile,
  ]);

  const allTools = useMemo(() => {
    return [...(tools?.data ?? []), ...(topTools?.data ?? [])];
  }, [tools, topTools]);

  const closeModal = () => {
    setSelectedToolId(null);
    const hasSkippedThisToolType = skippedTours[toolType];
    const shouldShowTour =
      !isLoading &&
      !isTopToolsLoading &&
      topTools?.data &&
      (!tools?.data || tools.data.length === 0) &&
      !skipAllTours &&
      !hasSkippedThisToolType;
    if (shouldShowTour) {
      setOnboardingStepIndex(3);
      setRunTour(true);
      setIsTourFlowActive(true);
    }
  };

  const deleteTool = useDeleteTool({
    mutation: {
      onMutate: async ({ id }: { id: number }) => {
        const getToolsQueryKey = getGetToolsQueryKey();
        const previousData = queryClient.getQueryData(getToolsQueryKey);

        queryClient.setQueryData(getToolsQueryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter((tool: Tool) => tool.id !== id),
          };
        });

        return { previousData };
      },
      onError: (error: any, _variables: any, context: any) => {
        const getToolsQueryKey = getGetToolsQueryKey();
        if (context?.previousData) {
          queryClient.setQueryData(getToolsQueryKey, context.previousData);
        }
        console.error(`Failed to delete ${toolType}:`, error);
      },
      onSettled: () => {
        const getToolsQueryKey = getGetToolsQueryKey();
        queryClient.invalidateQueries({ queryKey: getToolsQueryKey });
      },
    },
  });

  const handleDelete = async (toolId: number) => {
    try {
      await deleteTool.mutateAsync({ id: toolId });
      addToast({
        title: t(`${toolType}.deleted`),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t(`${toolType}.deleteError`),
        description: t(`${toolType}.deleteErrorDescription`),
        variant: "error",
      });
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleClick = (toolType: string) => {
    track(
      {
        category: "ContentGeneration",
        action: `${toolType} slide.generate`,
        label: `${toolType} generation click`,
        nonInteraction: false,
      },
      {
        location: `/${toolType}/generate`,
        element: "generate-button",
      },
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-100 dark:from-gray-900 dark:to-gray-900">
      {/* Tool Helper Component */}
      <ToolHelper
        toolPath={toolPath}
        learnMoreLink={
          "https://akademi.madlen.io/c/madlen-ii/sections/559560/lessons/2104709"
        }
        shouldShowTour={
          !skipAllTours &&
          !isMobile &&
          !tools?.data?.length &&
          topTools?.data?.length > 0
        }
        toolType={t(`quickActions.${toolType}`)}
      />

      {/* Conditional rendering of Joyride Because of Body overflow */}
      {!skipAllTours && !isMobile && runTour && (
        <Joyride
          locale={{
            back: t("onboarding.back"),
            close: t("onboarding.close"),
            last: t("onboarding.finish"),
            next: t("onboarding.next"),
            skip: t("onboarding.skip"),
          }}
          steps={steps}
          run={true}
          continuous
          stepIndex={onboardingStepIndex}
          showSkipButton
          styles={theme === "dark" ? joyrideStylesDark : joyrideStyles}
          callback={handleJoyrideCallback}
        />
      )}
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {/* Hero Section */}
        <HeroTitle
          onClick={() => handleClick(toolType)}
          title={t(`quickActions.${toolType}`)}
          description={t(`quickActions.${toolType}Desc`)}
          titleStyle={`bg-gradient-to-r ${colors.light} ${colors.dark}`}
          titleBackgroundStyle={`bg-gradient-to-r ${colors.iconBg}`}
          buttonStyle={`bg-gradient-to-r ${colors.light} hover:opacity-90 create-button`}
          buttonLink={`/content/${toolPath}/create`}
        />

        {/* Featured Tools Carousel */}
        <div className="featured-carousel">
          <FeaturedCarousel
            key={isTourFlowActive ? "tour" : "normal"} // force remount when tour flow is active or not in order to avoid overflow
            dataLength={topTools?.data?.length || 0}
            backgroundColor={colors.iconBg}
            gradientColor={colors.light}
            renderFeaturedCards={() =>
              (isTourFlowActive
                ? topTools?.data?.slice(0, 3)
                : topTools?.data
              )?.map((tool: Tool) => (
                <FeaturedCard
                  key={tool.id}
                  className="featured-card"
                  item={{
                    id: tool.id?.toString() ?? "",
                    imageUrl: tool.imageUrl ?? "",
                    videoUrl: tool.videoUrl ?? "",
                    prompt: (tool.title || tool.prompt) ?? "",
                    authorName: tool.authorName ?? "",
                    likeCount: tool.likeCount ?? 0,
                    courseName: tool.courseName ?? "",
                    gradeLevel:
                      tool.gradeLevel === -1
                        ? 0
                        : (Number(tool.gradeLevel) ?? 0),
                    didUserLiked: tool.didUserLiked ?? false,
                    createdAt: tool.createdAt ?? "",
                  }}
                  selectItem={(item) => setSelectedToolId(Number(item.id))}
                  onLike={async () => {
                    if (tool.id) {
                      if (tool.didUserLiked) {
                        await unlikeTool(tool.id);
                      } else {
                        await likeTool(tool.id);
                      }
                      queryClient.invalidateQueries({
                        queryKey: getGetTopToolsQueryKey(),
                      });
                    }
                  }}
                />
              ))
            }
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div
              className={`w-16 h-16 border-4 ${colors.iconColor} border-t-transparent rounded-full animate-spin`}
            ></div>
            <Text
              variant={"span"}
              className="!text-gray-600 dark:!text-gray-300"
              weight={"medium"}
              size={"lg"}
            >
              {`${toolType}.loading`}
            </Text>
          </div>
        )}

        {/* Error State */}
        {error && (
          <ErrorComponent
            title={t(`${toolType}.error.title`)}
            refetch={refetch}
            description={t(`${toolType}.error.description`)}
            retryButton={t(`${toolType}.error.retryButton`)}
          />
        )}

        {/* Tools Grid */}
        {!isLoading && !error && tools?.data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.data.map((tool: Tool) => (
              <Card
                key={tool.id}
                item={{
                  id: tool.id?.toString() ?? "",
                  imageUrl: tool.imageUrl ?? "",
                  videoUrl: tool.videoUrl ?? "",
                  prompt: (tool.title || tool.prompt) ?? "",
                  description: tool.description ?? "",
                  courseName: tool.courseName ?? "",
                  gradeLevel:
                    tool.gradeLevel === -1 ? 0 : (Number(tool.gradeLevel) ?? 0),
                  createdAt: tool.createdAt ?? "",
                }}
                selectItem={() => setSelectedToolId(Number(tool.id))}
                customPreview={<div className={`${gradientClasses} z-0`}></div>}
                setShowDeletePopup={(id) => setShowDeletePopup(Number(id))}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && (!tools?.data || tools.data.length === 0) && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl p-8">
            <div className={`p-4 rounded-full ${colors.iconBg}`}>
              <Package size={32} className={colors.iconColor} />
            </div>
            <Text variant={"h2"}>{`${toolType}.empty.title`}</Text>
            <Text variant="p" status="subtle" size={"base"}>
              {`${toolType}.empty.description`}
            </Text>
            <Button
              onClick={() => navigate(`/content/${toolPath}/create`)}
              variant="solid"
              size="md"
              rounded="lg"
              textColor="text-white"
              bgColor={`hover:opacity-90 ${colors.light ? `${colors.light} bg-gradient-to-r` : "bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"}`}
            >
              {t(`${toolType}.empty.createButton`)}
            </Button>
          </div>
        )}

        {/* Tool Detail Modal */}
        {selectedToolId && (
          <div
            className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeModal();
              }
            }}
          >
            <div
              className={`bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col ${maxWidth}`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <Text variant={"h1"}>
                  {allTools?.find((tool) => tool.id === selectedToolId)
                    ?.title ||
                    allTools?.find((tool) => tool.id === selectedToolId)
                      ?.topic ||
                    allTools?.find((tool) => tool.id === selectedToolId)
                      ?.prompt}
                </Text>
                <Button
                  onClick={closeModal}
                  variant="solid"
                  size="icon"
                  rounded="full"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={24} className="text-gray-500" />
                </Button>
              </div>

              <div
                className={`flex-1 overflow-auto ${toolType == "formativeAssessment" ? "" : "md:p-6"} p-0 modal-content`}
              >
                <DetailComponent
                  tool={{
                    ...allTools?.find((tool) => tool.id === selectedToolId),
                    gradeLevel:
                      allTools?.find((tool) => tool.id === selectedToolId)
                        ?.gradeLevel === -1
                        ? 0
                        : allTools?.find((tool) => tool.id === selectedToolId)
                            ?.gradeLevel,
                    isEditable: !!tools.data?.find(
                      (tool: any) => tool.id === selectedToolId,
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t(`${toolType}.confirmDelete`, {
              title:
                allTools?.find((tool) => tool.id === showDeletePopup)?.title ??
                "",
            })}
            showDeletePopup={showDeletePopup}
            handleDelete={handleDelete}
            setShowDeletePopup={setShowDeletePopup}
          />
        )}
      </div>
    </div>
  );
};

export default GenericToolPage;
